// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { CartLineModel, InvoiceLineModel, OrderLineModel } from "@insite/client-framework/Types/ApiModels";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { LazyImageProps } from "@mcmc/MobiusOverrides/LazyImage";
import CloudinaryImage from "@mcmc/XDL/Components/Common/CloudinaryImage";
import { transformationsMap } from "@mcmc/XDL/Utilities/cloudinary";
import mergeToNew from "@mcmc/XDL/Utilities/xMergeToNew";
import React, { useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const disableLazyLoad = parseQueryString<{ disableLazyLoad?: string }>(location.search).disableLazyLoad;
    return {
        disableLazyLoad: disableLazyLoad?.toLowerCase() === "true",
    };
};

interface OwnProps {
    product: CartLineModel | ProductContextModel | OrderLineModel | InvoiceLineModel;
    onClick?: React.EventHandler<React.MouseEvent>;
    showSmallImage?: boolean;
    imageTransformations?: string;
    isShareEntity?: boolean;
    extendedStyles?: ProductImageStyles;
    // XNG-Changes
    customPath?: string;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface ProductImageStyles {
    linkWrappingImage?: ClickablePresentationProps;
    image?: LazyImageProps;
}

export const productImageStyles: ProductImageStyles = {
    linkWrappingImage: {
        css: css`
            width: 100%;
            height: 100%;
        `,
    },
    image: {},
};

const ProductImage = ({
    product,
    onClick,
    showSmallImage,
    extendedStyles,
    disableLazyLoad,
    imageTransformations,
    isShareEntity,
    customPath,
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(productImageStyles, extendedStyles));

    let altText = "product" in product ? product.product.imageAltText : product.altText;
    if (!altText || altText === "") {
        altText = "product" in product ? product.product.productTitle : product.productName;
    }

    let imagePath;
    if ("product" in product) {
        imagePath = showSmallImage
            ? product.product.smallImagePath || product.product.mediumImagePath
            : product.product.mediumImagePath || product.product.smallImagePath;
    } else if ("mediumImagePath" in product) {
        imagePath = product.mediumImagePath;
    } else if ("smallImagePath" in product) {
        imagePath = product.smallImagePath;
    }

    let productDetailPath = "product" in product ? product.productInfo.productDetailPath : product.productUri;
    if (customPath) {
        productDetailPath = customPath;
    }
    if (
        ("isActiveProduct" in product && !product.isActiveProduct) ||
        ("vmiLocationId" in product && product.vmiLocationId && "isActiveVmiBin" in product && !product.isActiveVmiBin)
    ) {
        productDetailPath = "";
    }
    const productImage = (
        <CloudinaryImage
            {...styles.image}
            src={imagePath}
            imageTransformations={imageTransformations ?? transformationsMap.product_sm}
            altText={altText}
            disableLazyLoad={disableLazyLoad || isShareEntity}
        />
    );
    if (productDetailPath) {
        return (
            <Clickable
                {...styles.linkWrappingImage}
                href={productDetailPath}
                onClick={onClick}
                data-test-selector="productImage"
                data-product-number={`${"product" in product ? product.product?.productNumber : ""}`}
            >
                {productImage}
            </Clickable>
        );
    }
    return <StyledWrapper {...styles.linkWrappingImage}>{productImage}</StyledWrapper>;
};

export default connect(mapStateToProps)(ProductImage);
